import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AreaChart } from './components/AreaChart';
import { cn } from '@/lib/utils/cn';
import { analyticsApi, type AnalyticsRange } from '@/lib/api/analytics';
import { queryKeys } from '@/lib/api/queryKeys';

const RANGES: ReadonlyArray<{ id: AnalyticsRange; label: string; days: number }> = [
  { id: '7d', label: '7 days', days: 7 },
  { id: '30d', label: '30 days', days: 30 },
  { id: '90d', label: '90 days', days: 90 },
];

export function AnalyticsPage() {
  const [range, setRange] = useState<AnalyticsRange>('30d');
  const meta = RANGES.find((r) => r.id === range)!;

  const overviewQuery = useQuery({
    queryKey: queryKeys.analyticsOverview(range),
    queryFn: () => analyticsApi.overview(range),
    staleTime: 60_000,
  });

  const referrersQuery = useQuery({
    queryKey: queryKeys.analyticsReferrers(range),
    queryFn: () => analyticsApi.referrers(range, 10),
    staleTime: 60_000,
  });

  const overview = overviewQuery.data;
  const referrers = referrersQuery.data ?? [];

  const views = useMemo(
    () => overview?.series.map((p) => p.views) ?? [],
    [overview],
  );
  const uniques = useMemo(
    () => overview?.series.map((p) => p.uniques) ?? [],
    [overview],
  );

  const totals = overview?.totals ?? { views: 0, uniques: 0 };
  const hasAnyTraffic = views.length > 0 && views.some((v) => v > 0);
  const topReferrerCount = referrers.length ? referrers[0].count : 0;

  return (
    <div className="mx-auto max-w-[1100px] space-y-10">
      <header className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-faint">
            Analytics
          </p>
          <h1 className="mb-2 font-display text-3xl font-semibold tracking-tight">
            How your work travels.
          </h1>
          <p className="max-w-xl font-editorial text-lg italic text-ink-muted">
            Quiet, privacy-first metrics — no cookies, no fingerprints.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-lg border border-line bg-board p-1">
            {RANGES.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setRange(r.id)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                  r.id === range ? 'bg-ink text-board' : 'text-ink-muted hover:text-ink',
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Stat label="Page views" value={totals.views.toLocaleString()} loading={overviewQuery.isPending} />
        <Stat
          label="Unique visitors"
          value={totals.uniques.toLocaleString()}
          loading={overviewQuery.isPending}
        />
      </div>

      <section className="rounded-2xl border border-line bg-board p-6 shadow-card">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="font-display text-xl font-semibold tracking-tight">Traffic</h2>
            <p className="mt-1 text-xs text-ink-muted">
              Views and visitors across {meta.label.toLowerCase()}.
            </p>
          </div>
          <div className="flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.15em] text-ink-muted">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-ink" /> Views
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full border border-ink bg-transparent" /> Visitors
            </span>
          </div>
        </div>
        {overviewQuery.isPending ? (
          <ChartSkeleton />
        ) : hasAnyTraffic ? (
          <AreaChart series={[views, uniques]} days={meta.days} />
        ) : (
          <EmptyState
            title="No traffic yet"
            body="Once visitors open your published site, views will start showing up here within a minute."
          />
        )}
      </section>

      <div className="grid grid-cols-1 gap-6">
        <BarList
          title="Top referrers"
          loading={referrersQuery.isPending}
          items={referrers.map((r) => ({
            label: formatReferrer(r.key),
            value: r.count,
            pct: topReferrerCount ? Math.round((r.count / topReferrerCount) * 100) : 0,
          }))}
          emptyBody="No referrers yet. Share your site somewhere to see traffic sources here."
        />
      </div>
    </div>
  );
}

function formatReferrer(raw: string): string {
  if (!raw) return 'Direct';
  try {
    const url = raw.startsWith('http') ? new URL(raw) : new URL(`https://${raw}`);
    return url.hostname.replace(/^www\./, '');
  } catch {
    return raw;
  }
}

function Stat({
  label,
  value,
  loading,
}: {
  label: string;
  value: string;
  loading?: boolean;
}) {
  return (
    <div className="rounded-2xl border border-line bg-board p-5 shadow-card">
      <div className="mb-3 flex items-start justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-faint">
          {label}
        </span>
      </div>
      <div className="font-display text-[28px] font-semibold leading-none tracking-tight">
        {loading ? <span className="text-ink-faint">—</span> : value}
      </div>
    </div>
  );
}

function BarList({
  title,
  items,
  loading,
  emptyBody,
}: {
  title: string;
  items: ReadonlyArray<{ label: string; value: number; pct: number }>;
  loading?: boolean;
  emptyBody: string;
}) {
  return (
    <div className="rounded-2xl border border-line bg-board p-6 shadow-card">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold tracking-wide">{title}</h2>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-faint">
          Views
        </span>
      </div>
      {loading ? (
        <ListSkeleton />
      ) : items.length === 0 ? (
        <EmptyState body={emptyBody} />
      ) : (
        <ul className="space-y-3">
          {items.map((item) => (
            <li key={item.label} className="relative">
              <div className="flex items-center justify-between text-sm">
                <span className="truncate font-mono text-[13px] text-ink">{item.label}</span>
                <span className="font-mono tabular-nums text-xs text-ink-muted">
                  {item.value.toLocaleString()}
                </span>
              </div>
              <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-rail">
                <span
                  className="block h-full bg-ink"
                  style={{ width: `${Math.min(100, item.pct)}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="flex h-[260px] items-center justify-center">
      <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-faint">
        Loading…
      </span>
    </div>
  );
}

function ListSkeleton() {
  return (
    <ul className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i} className="h-5 animate-pulse rounded bg-rail/60" />
      ))}
    </ul>
  );
}

function EmptyState({ title, body }: { title?: string; body: string }) {
  return (
    <div className="flex flex-col items-start gap-1 rounded-lg border border-dashed border-line bg-canvas p-4">
      {title ? <p className="text-sm font-semibold text-ink">{title}</p> : null}
      <p className="text-xs text-ink-muted">{body}</p>
    </div>
  );
}
